import { useCallback, type Dispatch, type RefObject } from "react";
import { useProvetStatic } from "../context";
import { stripEmojiFromContactDetails } from "../modules/provetContactDetails/helpers/contactDetailsUtility";
import {
  ContactDetailsEventArgs,
  ProvetDepartment,
  ProvetDialogStep,
  Reason,
  SelectedAppointmentTime
} from "../types";
import { hashEmail } from "../utilities/emailHasher";
import { scrollIntoView } from "../utilities/scrollUtilities";
import {
  ProvetBookingFlowAction,
  ProvetBookingFlowActionType,
  ProvetBookingFlowState
} from "./provetBookingFlowReducer";
import { TrackingState } from "./useProvetTracking";

interface UseBookingFormActionsOptions {
  bookingFlowState: ProvetBookingFlowState;
  bookingFlowDispatch: Dispatch<ProvetBookingFlowAction>;
  tracking: TrackingState;
  setEditDialog: (
    step: ProvetDialogStep,
    isComplete?: boolean,
    transition?: boolean
  ) => Promise<void>;
  updateBookingStep: (
    provetDialogStep: ProvetDialogStep,
    isComplete?: boolean
  ) => Promise<void>;
  modals: {
    summary: {
      open: () => void;
    };
  };
  scrollRefs: {
    formRef: RefObject<HTMLFormElement>;
  };
}

interface BookingFormActionsHandlers {
  setDepartment: (department: ProvetDepartment, transition?: boolean) => Promise<void>;
  setReason: (reason?: Reason, transition?: boolean) => Promise<void>;
  setAppointment: (appointmentTime?: SelectedAppointmentTime) => Promise<void>;
  setContactDetails: (contactDetails: ContactDetailsEventArgs) => Promise<void>;
}

/**
 * Hook for managing booking form data actions.
 * Handles department, reason, appointment, and contact details selection.
 */
export const useBookingFormActions = ({
  bookingFlowState,
  bookingFlowDispatch,
  tracking,
  setEditDialog,
  updateBookingStep,
  modals,
  scrollRefs
}: UseBookingFormActionsOptions): BookingFormActionsHandlers => {
  const { api } = useProvetStatic();

  const setDepartment = useCallback(
    async (department: ProvetDepartment, transition = true) => {
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: { selectedDepartment: department }
      });
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: {
          selectClinic: true,
          selectTreatment: false,
          selectDateAndTime: false,
          selectContactDetails: false
        }
      });

      // Fetch and set care unit info for tracking. The previous clinic is cleared first, so
      // events never carry it if the lookup fails, finds nothing or is overtaken.
      const pmdId = department.id?.toString() || "";
      if (pmdId) {
        await tracking.loadSelectedCareUnit(() => api.fetchCareUnit(pmdId));
      } else {
        tracking.setSelectedCareUnit(undefined);
      }

      // Give React time to process state updates
      await new Promise(resolve => setTimeout(resolve, 300));

      // Move to next step
      await setEditDialog(ProvetDialogStep.SelectTreatment, true, transition);
    },
    [bookingFlowDispatch, api, tracking, setEditDialog]
  );

  const setReason = useCallback(
    async (reason?: Reason, transition = true) => {
      // Check if we need to preserve or clear contact details based on species compatibility
      const currentContactDetails = bookingFlowState.formData?.contactDetails;
      const currentPatientSpeciesCode = currentContactDetails?.patient?.species?.code;

      let contactDetailsUpdate = {};

      // If contact details exist and the new treatment has species restrictions
      if (
        currentContactDetails &&
        reason?.species_codes &&
        reason.species_codes.length > 0
      ) {
        if (currentPatientSpeciesCode) {
          // Check if current species is compatible with new treatment
          const isSpeciesCompatible = reason.species_codes.includes(
            currentPatientSpeciesCode
          );

          if (!isSpeciesCompatible) {
            // Species incompatible: preserve client data and patient name, but clear species/breed
            contactDetailsUpdate = {
              contactDetails: {
                client: currentContactDetails.client, // Keep client info (name, email, phone)
                patient: {
                  ...currentContactDetails.patient,
                  species: undefined, // Clear incompatible species
                  breed: undefined, // Clear breed (depends on species)
                  notes: currentContactDetails.patient.notes // Keep notes
                }
              }
            };
          }
          // If compatible, don't include contactDetailsUpdate, which preserves everything
        }
      }

      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          selectedReason: reason,
          ...contactDetailsUpdate // Only included if species is incompatible
        }
      });

      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: {
          selectTreatment: true,
          selectDateAndTime: false,
          selectContactDetails: false // User needs to review/confirm contact details
        }
      });

      // Give React time to process state updates
      await new Promise(resolve => setTimeout(resolve, 300));

      await setEditDialog(ProvetDialogStep.SelectDateAndTime, true, transition);
    },
    [bookingFlowDispatch, setEditDialog, bookingFlowState.formData]
  );

  const setAppointment = useCallback(
    async (appointmentTime?: SelectedAppointmentTime) => {
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: { selectedAppointmentTime: appointmentTime }
      });

      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: {
          selectDateAndTime: true,
          selectContactDetails: false
        }
      });

      // Ensure state updates have been applied
      await new Promise(resolve => setTimeout(resolve, 200));

      // Move to next step
      await updateBookingStep(ProvetDialogStep.ContactDetails, true);
    },
    [bookingFlowDispatch, updateBookingStep]
  );

  const setContactDetails = useCallback(
    async (enteredContactDetails: ContactDetailsEventArgs) => {
      // Emoji are removed (the server does the same), so the summary shows what the clinic gets
      const contactDetails = stripEmojiFromContactDetails(enteredContactDetails);

      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          contactDetails,
          // Copy honeypot field to root level for backend validation
          extraField: contactDetails.client.extraField
        }
      });
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: {
          selectContactDetails: true
        }
      });

      // Cache species name for tracking
      const speciesName = contactDetails.patient.species?.label;
      if (speciesName) {
        tracking.setPetSpeciesName(speciesName);
      }

      // Hash email for tracking
      let hashedEmail = "";
      if (contactDetails.client.email) {
        try {
          hashedEmail = await hashEmail(contactDetails.client.email);
          tracking.setHashedEmail(hashedEmail);
        } catch (error) {
          console.error("Error hashing email:", error);
        }
      }

      // Update tracking service IMMEDIATELY with the new data
      tracking.trackingService.updateOptions({
        petSpeciesName: speciesName || "",
        hashedEmail: hashedEmail
      });

      // Give React time to process state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      await updateBookingStep(ProvetDialogStep.Confirmation);

      // Open the summary modal and scroll to form
      modals.summary.open();
      if (scrollRefs.formRef.current) {
        scrollIntoView(scrollRefs.formRef.current);
      }
    },
    [bookingFlowDispatch, updateBookingStep, tracking, modals.summary, scrollRefs.formRef]
  );

  return {
    setDepartment,
    setReason,
    setAppointment,
    setContactDetails
  };
};
